import { create } from 'zustand';
import { loadHistory, saveHistory, clearHistoryStored, HISTORY_MAX } from '../utils/storage.js';

const HISTORY_LIMIT = 80;
const sessionBlobs = new Map();

function snapshot(state) {
  return {
    pages: state.pages,
    annotations: state.annotations,
  };
}

function restore(state, snap) {
  return {
    ...state,
    pages: snap.pages,
    annotations: snap.annotations,
    selected: new Set(),
  };
}

export const usePdf = create((set, get) => ({
  fileName: '',
  bytes: null,
  pages: [],
  annotations: {},
  selected: new Set(),
  busy: false,
  error: '',

  view: 'all',
  currentPageId: null,
  tool: 'select',
  pendingSignature: null,

  textSelection: new Map(),

  past: [],
  future: [],

  exports: loadHistory(),

  addExport: (meta, exportedBlob, originalBlob) => {
    const entry = { ...meta };
    if (exportedBlob || originalBlob) {
      sessionBlobs.set(meta.id, { exported: exportedBlob || null, original: originalBlob || null });
    }
    const next = [entry, ...get().exports].slice(0, HISTORY_MAX);
    saveHistory(next);
    set({ exports: next });
  },

  removeExport: (id) => {
    sessionBlobs.delete(id);
    const next = get().exports.filter((e) => e.id !== id);
    saveHistory(next);
    set({ exports: next });
  },

  clearExports: () => {
    sessionBlobs.clear();
    clearHistoryStored();
    set({ exports: [] });
  },

  getExportBlob: (id, which = 'exported') => {
    const slot = sessionBlobs.get(id);
    if (!slot) return null;
    return slot[which] || null;
  },

  load: ({ fileName, bytes, pages }) =>
    set({
      fileName,
      bytes,
      pages,
      annotations: {},
      selected: new Set(),
      error: '',
      view: 'all',
      currentPageId: null,
      tool: 'select',
      pendingSignature: null,
      past: [],
      future: [],
    }),

  unload: () =>
    set({
      fileName: '',
      bytes: null,
      pages: [],
      annotations: {},
      selected: new Set(),
      error: '',
      view: 'all',
      currentPageId: null,
      tool: 'select',
      pendingSignature: null,
      past: [],
      future: [],
    }),

  setBusy: (busy) => set({ busy }),
  setError: (error) => set({ error }),

  toggleTextSelection: (entry, additive = false) => {
    const sel = new Map(get().textSelection);
    const key = `${entry.pageId}::${entry.itemKey}`;
    if (additive) {
      sel.has(key) ? sel.delete(key) : sel.set(key, entry);
    } else {
      if (sel.size === 1 && sel.has(key)) sel.clear();
      else {
        sel.clear();
        sel.set(key, entry);
      }
    }
    set({ textSelection: sel });
  },

  clearTextSelection: () => set({ textSelection: new Map() }),

  // patch carries only the attribute(s) the user toggled. For an
  // existing override it is merged in (everything else is preserved);
  // for a brand-new override it is layered on top of the glyph's own
  // font/size so untouched attributes keep the original styling.
  restyleTextSelection: (patch) =>
    get()._commit((s) => {
      const ann = { ...s.annotations };
      const byPage = {};
      for (const [, e] of s.textSelection) {
        if (!byPage[e.pageId]) byPage[e.pageId] = [];
        byPage[e.pageId].push(e);
      }
      for (const [pid, entries] of Object.entries(byPage)) {
        let list = ann[pid] ? [...ann[pid]] : [];
        for (const e of entries) {
          const idx = list.findIndex((a) => a.originalItemKey === e.itemKey);
          if (idx >= 0) {
            list[idx] = { ...list[idx], ...patch, _edit: false };
          } else {
            list.push({
              id: crypto.randomUUID(),
              type: 'text',
              originalItemKey: e.itemKey,
              x: e.x,
              y: e.y,
              w: e.w,
              h: e.h,
              size: e.fontSize,
              font: e.font || 'Helvetica',
              color: e.color || '#111111',
              bold: !!e.bold,
              italic: !!e.italic,
              underline: false,
              strike: false,
              background: e.bg || '#ffffff',
              text: e.str,
              ...patch,
            });
          }
        }
        ann[pid] = list;
      }
      return { annotations: ann };
    }),

  // Move every selected glyph by (dx, dy) from its original position.
  // Creates a bound override on first move. No history push — the caller
  // calls beginEdit() once when the drag starts so the whole drag is a
  // single undo step.
  moveTextSelection: (dx, dy) =>
    set((s) => {
      const ann = { ...s.annotations };
      const byPage = {};
      for (const [, e] of s.textSelection) {
        if (!byPage[e.pageId]) byPage[e.pageId] = [];
        byPage[e.pageId].push(e);
      }
      for (const [pid, entries] of Object.entries(byPage)) {
        let list = ann[pid] ? [...ann[pid]] : [];
        for (const e of entries) {
          const idx = list.findIndex((a) => a.originalItemKey === e.itemKey);
          if (idx >= 0) {
            list[idx] = { ...list[idx], x: e.x + dx, y: e.y + dy };
          } else {
            list.push({
              id: crypto.randomUUID(),
              type: 'text',
              originalItemKey: e.itemKey,
              x: e.x + dx,
              y: e.y + dy,
              w: e.w,
              h: e.h,
              size: e.fontSize,
              font: e.font || 'Helvetica',
              color: e.color || '#111111',
              bold: !!e.bold,
              italic: !!e.italic,
              underline: false,
              strike: false,
              background: e.bg || '#ffffff',
              text: e.str,
            });
          }
        }
        ann[pid] = list;
      }
      return { annotations: ann };
    }),

  deleteTextSelection: () =>
    get()._commit((s) => {
      const ann = { ...s.annotations };
      const byPage = {};
      for (const [, e] of s.textSelection) {
        if (!byPage[e.pageId]) byPage[e.pageId] = [];
        byPage[e.pageId].push(e);
      }
      for (const [pid, entries] of Object.entries(byPage)) {
        const list = ann[pid] ? [...ann[pid]] : [];
        for (const e of entries) {
          if (list.some((a) => a.originalItemKey === e.itemKey)) continue;
          list.push({
            id: crypto.randomUUID(),
            type: 'text',
            originalItemKey: e.itemKey,
            x: e.x,
            y: e.y,
            w: e.w,
            h: e.h,
            size: e.fontSize,
            font: 'Helvetica',
            color: e.bg || '#ffffff',
            background: e.bg || '#ffffff',
            text: '',
          });
        }
        ann[pid] = list;
      }
      return { annotations: ann, textSelection: new Map() };
    }),

  highlightTextSelection: (color = '#fde047') =>
    get()._commit((s) => {
      const ann = { ...s.annotations };
      const byPage = {};
      for (const [, e] of s.textSelection) {
        if (!byPage[e.pageId]) byPage[e.pageId] = [];
        byPage[e.pageId].push(e);
      }
      for (const [pid, entries] of Object.entries(byPage)) {
        const list = ann[pid] ? [...ann[pid]] : [];
        for (const e of entries) {
          list.push({
            id: crypto.randomUUID(),
            type: 'highlight',
            x: e.x,
            y: e.y,
            w: e.w,
            h: e.h,
            color,
            opacity: 0.4,
          });
        }
        ann[pid] = list;
      }
      return { annotations: ann, textSelection: new Map() };
    }),

  setView: (view) => set({ view }),
  openPage: (id) => set({ view: 'page', currentPageId: id, selected: new Set([id]) }),
  setCurrentPageId: (id) => set({ currentPageId: id }),
  goAll: () => set({ view: 'all' }),
  goGrid: () => set({ view: 'grid' }),
  setTool: (tool) => set({ tool }),
  setPendingSignature: (data) => set({ pendingSignature: data }),

  toggleSelect: (id, additive = false) => {
    const sel = new Set(get().selected);
    if (additive) {
      sel.has(id) ? sel.delete(id) : sel.add(id);
    } else {
      if (sel.size === 1 && sel.has(id)) sel.clear();
      else {
        sel.clear();
        sel.add(id);
      }
    }
    set({ selected: sel });
  },

  selectAll: () => set({ selected: new Set(get().pages.map((p) => p.id)) }),
  clearSelection: () => set({ selected: new Set() }),

  _commit: (mutator) => {
    const prev = snapshot(get());
    const next = mutator(get());
    if (!next) return;
    const past = [...get().past, prev].slice(-HISTORY_LIMIT);
    set({ ...next, past, future: [] });
  },

  setPages: (pages) => get()._commit(() => ({ pages, selected: new Set() })),

  rotateSelected: (delta) =>
    get()._commit((s) => ({
      pages: s.pages.map((p) =>
        s.selected.has(p.id) ? { ...p, rotation: (p.rotation + delta + 360) % 360 } : p
      ),
    })),

  rotatePage: (id, delta) =>
    get()._commit((s) => ({
      pages: s.pages.map((p) => (p.id === id ? { ...p, rotation: (p.rotation + delta + 360) % 360 } : p)),
    })),

  deleteSelected: () =>
    get()._commit((s) => {
      const next = s.pages.filter((p) => !s.selected.has(p.id));
      const ann = { ...s.annotations };
      s.selected.forEach((id) => delete ann[id]);
      return { pages: next, annotations: ann, selected: new Set() };
    }),

  deletePage: (id) =>
    get()._commit((s) => {
      const next = s.pages.filter((p) => p.id !== id);
      const ann = { ...s.annotations };
      delete ann[id];
      return { pages: next, annotations: ann, selected: new Set() };
    }),

  duplicateSelected: () =>
    get()._commit((s) => {
      const out = [];
      const annOut = { ...s.annotations };
      for (const p of s.pages) {
        out.push(p);
        if (s.selected.has(p.id)) {
          const copyId = crypto.randomUUID();
          out.push({ ...p, id: copyId });
          if (annOut[p.id]) {
            annOut[copyId] = annOut[p.id].map((a) => ({ ...a, id: crypto.randomUUID() }));
          }
        }
      }
      return { pages: out, annotations: annOut };
    }),

  duplicatePage: (id) =>
    get()._commit((s) => {
      const out = [];
      const annOut = { ...s.annotations };
      for (const p of s.pages) {
        out.push(p);
        if (p.id === id) {
          const copyId = crypto.randomUUID();
          out.push({ ...p, id: copyId });
          if (annOut[id]) annOut[copyId] = annOut[id].map((a) => ({ ...a, id: crypto.randomUUID() }));
        }
      }
      return { pages: out, annotations: annOut };
    }),

  reorder: (nextPages) => get()._commit(() => ({ pages: nextPages })),

  addAnnotation: (pageId, ann) =>
    get()._commit((s) => {
      const list = s.annotations[pageId] ? [...s.annotations[pageId]] : [];
      list.push(ann);
      return { annotations: { ...s.annotations, [pageId]: list } };
    }),

  updateAnnotation: (pageId, annId, patch) =>
    get()._commit((s) => {
      const list = (s.annotations[pageId] || []).map((a) => (a.id === annId ? { ...a, ...patch } : a));
      return { annotations: { ...s.annotations, [pageId]: list } };
    }),

  updateAnnotationLive: (pageId, annId, patch) => {
    const s = get();
    const list = (s.annotations[pageId] || []).map((a) => (a.id === annId ? { ...a, ...patch } : a));
    set({ annotations: { ...s.annotations, [pageId]: list } });
  },

  beginEdit: () => {
    const prev = snapshot(get());
    const past = [...get().past, prev].slice(-HISTORY_LIMIT);
    set({ past, future: [] });
  },

  deleteAnnotation: (pageId, annId) =>
    get()._commit((s) => {
      const list = (s.annotations[pageId] || []).filter((a) => a.id !== annId);
      return { annotations: { ...s.annotations, [pageId]: list } };
    }),

  // Layer order: annotations render in array order (last = on top).
  // dir: 'front' | 'back' | 'forward' | 'backward'.
  reorderAnnotation: (pageId, annId, dir) =>
    get()._commit((s) => {
      const list = [...(s.annotations[pageId] || [])];
      const i = list.findIndex((a) => a.id === annId);
      if (i < 0) return null;
      const [item] = list.splice(i, 1);
      if (dir === 'front') {
        list.push(item);
      } else if (dir === 'back') {
        list.unshift(item);
      } else if (dir === 'forward') {
        list.splice(Math.min(list.length, i + 1), 0, item);
      } else {
        list.splice(Math.max(0, i - 1), 0, item);
      }
      return { annotations: { ...s.annotations, [pageId]: list } };
    }),

  undo: () => {
    const { past, future } = get();
    if (!past.length) return;
    const prev = past[past.length - 1];
    const present = snapshot(get());
    set((s) => ({
      ...restore(s, prev),
      past: past.slice(0, -1),
      future: [present, ...future].slice(0, HISTORY_LIMIT),
    }));
  },

  redo: () => {
    const { past, future } = get();
    if (!future.length) return;
    const next = future[0];
    const present = snapshot(get());
    set((s) => ({
      ...restore(s, next),
      past: [...past, present].slice(-HISTORY_LIMIT),
      future: future.slice(1),
    }));
  },
}));
